import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import 'moment/locale/en-gb';
import {
  Button, Table, Typography, Row, Popover,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import '../bynx.css';
import {selectSessionUser} from "../../../../state/ui/application/sessionUiReducer";
import {getDefaultLocale} from "../../../../configWeb";
import {
  createCredits,
  requestAllCreditBalances,
  requestBucketBalances
} from "../../../../state/ui/credits/customerCreditsUiActions";
import {
  selectCreditAllocations,
} from "../../../../state/domain/credits/customerCreditReducer";
import {
  selectBucketBalancesRequest,
  selectCreditBalancesRequest
} from "../../../../state/ui/credits/customerCreditsUiReducer";
import {selectOverallBucketBalance} from "../../../../state/domain/credits/overallBalanceReducer";
import {selectBucketBalances} from "../../../../state/domain/credits/creditBalanceReducer";
import moment from "moment";
import {DATE_FORMAT_SHORTER} from "../packageUtil";
import CreateCreditAllocation from "./CreateCreditAllocation";


const { Column } = Table;
const { Text } = Typography;

// TABLE
function CreditsTable({
  isLoading, customers, onEditButton, locale,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${customers.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  function sortByDescription(a, b) {
    const aStr = `${a.description}`;
    const bStr = `${b.description}`;
    return aStr.localeCompare(bStr);
  }

  function sortByNumber(a, b, dataIndex) {
    if (a[dataIndex] && !b[dataIndex]) return 1;
    if (!a[dataIndex] && b[dataIndex]) return -1;
    if (a[dataIndex] === b[dataIndex]) return 0;
    return a[dataIndex] > b[dataIndex] ? 1 : -1;
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={customers}
      rowKey={record => record.id}
    >
      <Column
        sorter={sortByDescription}
        title={<FormattedMessage id="customer" />}
        dataIndex="description"
        key="description"
      />
      <Column
          title={<FormattedMessage id="currentBalance" />}
          dataIndex="currentBalance"
          key="currentBalance"
          align="right"
          width={160}
          sorter={(a, b) => sortByNumber(a, b, 'currentBalance')}
          defaultSortOrder="ascend"
          render={(number) => { return (number ? number.toLocaleString(locale) : 0); }}
      />
      <Column
        className="via-action-column"
        align="right"
        width={400}
        render={(text, record) => {
          return (
            <Row>
              <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="plus">
                <FormattedMessage id="addCredit" />
              </Button>
            </Row>
          );
        }}
      />
    </Table>
  );
}

CreditsTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    currentBalance: PropTypes.number.isRequired,
  })),
};

CreditsTable.defaultProps = {
  isLoading: false,
  customers: [],
};

// POPOVER
const DetailsPopover = injectIntl(({
   intl, setPopOverVisible, balanceDetails, isBalanceLoading, locale,
 }) => (
    <div>
      <Table
          pagination={false}
          loading={isBalanceLoading}
          dataSource={balanceDetails}
          rowKey={record => record.id}
      >
        <Column
            title={<FormattedMessage id="expiryDate" />}
            dataIndex="dateExpiry"
            key="dateExpiry"
            render={(text) => { return (moment.utc(text).format(DATE_FORMAT_SHORTER)); }}
        />
        <Column
            title={<FormattedMessage id="availBalance" />}
            dataIndex="remainingTokens"
            key="remainingTokens"
            align="right"
            render={(number) => { return (number ? number.toLocaleString(locale) : 0); }}
        />
        <Column
            title={<FormattedMessage id="daysToExpire" />}
            dataIndex="daysToExpiry"
            key="daysToExpiry"
            align="right"
        />
      </Table>
      <Row justify="center" type="flex"><Button type="link" onClick={() => setPopOverVisible(false)}><FormattedMessage id="close" /></Button></Row>
    </div>
));


// MAIN LAYOUT
function CustomerCredits({
  isLoading, loadPageData, customers, onCreate, sessionUser, availBalance, balanceDetails, isBalanceLoading, loadBalanceDetails,
}) {
  // Manage company - hard-coded for now
  const [manageCompany] = useState('PURCO');

  // Load users on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData(manageCompany);
    loadBalanceDetails(manageCompany);
  }, [loadPageData, loadBalanceDetails, manageCompany]);

  const intl = useIntl();

  // Popover control and display
  const [popOverVisible, setPopOverVisible] = useState(false);
  const showPopover = () => {
    loadBalanceDetails(manageCompany);
    setPopOverVisible(true);
  };

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  return (
    <ConfigHeading
      titleId="allocateCreditsTitle"
      descriptionId=""
      parameters={
        <Row>
          <Text style={{ fontWeight: 'bold' }}><FormattedMessage id="availCredits" />: </Text>
          <Text>{availBalance.balance ? availBalance.balance.toLocaleString(userLocale) : 0}</Text>
          &nbsp;&nbsp;
          <Popover
              content={<DetailsPopover setPopOverVisible={setPopOverVisible} balanceDetails={balanceDetails} isBalanceLoading={isBalanceLoading} locale={userLocale} />}
              title={<FormattedMessage id="availCreditBatches" />}
              trigger="click"
              visible={popOverVisible}
              placement="rightTop"
              onVisibleChange={(show) => {
                if (show) {
                  showPopover();
                }
                setPopOverVisible(show);
              }}
          >
            <Button type="link"><FormattedMessage id="viewDetails" /></Button>
          </Popover>
        </Row>
      }
    >
      <CreditsTable
        isLoading={isLoading}
        customers={customers}
        onEditButton={onCreate}
        intl={intl}
        locale={userLocale}
      />
      <CreateCreditAllocation manageCompany={manageCompany} sessionUser={sessionUser} />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
CustomerCredits.propTypes = {
  isLoading: PropTypes.bool,
  isBalanceLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    currentBalance: PropTypes.number.isRequired,
  })),
  availBalance: PropTypes.object,
  loadBalanceDetails: PropTypes.func.isRequired,
  balanceDetails: PropTypes.arrayOf(PropTypes.shape({
    dateExpiry: PropTypes.string.isRequired,
    remainingTokens: PropTypes.number.isRequired,
    daysToExpiry: PropTypes.number.isRequired,
  })),
  onCreate: PropTypes.func.isRequired,
};

CustomerCredits.defaultProps = {
  isLoading: false,
  isBalanceLoading: false,
  customers: [],
  availBalance: 0,
  balanceDetails: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectCreditBalancesRequest(state);
  const balanceDataRequest = selectBucketBalancesRequest(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    isBalanceLoading: balanceDataRequest ? balanceDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    customers: selectCreditAllocations(state),
    sessionUser: selectSessionUser(state),
    availBalance: selectOverallBucketBalance(state),
    balanceDetails: selectBucketBalances(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (manageCompany) => dispatch(requestAllCreditBalances(manageCompany)),
  loadBalanceDetails: (manageCompany) => dispatch(requestBucketBalances(manageCompany)),
  onCreate: creditRow => dispatch(createCredits(creditRow ? creditRow.id : null)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(CustomerCredits));
